import { useMemo, useRef, useState } from 'react'

import {
  Box,
  BoxProps,
  useBreakpointValue,
  useDimensions,
} from '@chakra-ui/react'

import { ThemeColor } from 'src/types'

import ExpandCollapseButton from './ExpandCollapseButton/ExpandCollapseButton'

type ExpandableTextProps = {
  text: string
  isPreview?: boolean
  className?: 'body-regular' | 'body-small' | 'body-extra-small'
  collapseThreshold?: number
  noOfLines?: number
  expandCollapseButtonProps?: {
    labelExpanded?: string
    labelCollapsed?: string
    textColor?: ThemeColor
  }
} & BoxProps

const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  className = 'body-regular',
  noOfLines = 3,
  collapseThreshold = 0,
  expandCollapseButtonProps = {
    labelExpanded: 'Hide',
    labelCollapsed: 'Show',
    textColor: 'onyx',
  },
  ...props
}) => {
  const [expanded, setExpanded] = useState(false)

  const invisibleTextRef = useRef<HTMLDivElement>(null)

  // this measures the full height if the text weren't truncated.
  const dimensions = useDimensions(invisibleTextRef, true)

  const threeLineHeight = useBreakpointValue({ base: 78, md: 82 })

  const textShouldOverflow = useMemo(() => {
    return (
      invisibleTextRef?.current?.clientHeight > threeLineHeight &&
      text.length >= collapseThreshold
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, invisibleTextRef, collapseThreshold, dimensions, threeLineHeight])

  return (
    <Box position="relative">
      <Box
        ref={invisibleTextRef}
        position="absolute"
        visibility="hidden"
        w="100%"
        whiteSpace="pre-wrap"
        className={className}
      >
        {text}
      </Box>
      {textShouldOverflow ? (
        <Box className={className} position="relative" {...props}>
          <Box
            overflow="hidden"
            overflowWrap="break-word"
            whiteSpace="pre-wrap"
            noOfLines={expanded ? undefined : noOfLines}
          >
            {text}

            <ExpandCollapseButton
              className={className}
              click={() => setExpanded(!expanded)}
              expanded={expanded}
              {...expandCollapseButtonProps}
            />
          </Box>
        </Box>
      ) : (
        <Box className={className} {...props} whiteSpace="pre-wrap">
          {text}
        </Box>
      )}
    </Box>
  )
}

export default ExpandableText
